import React from 'react';
import { OutlierMetrics } from '../types';

interface OutlierBadgeProps {
  outlier: OutlierMetrics;
  compact?: boolean;
}

export const OutlierBadge: React.FC<OutlierBadgeProps> = ({ outlier, compact = false }) => {
  if (!outlier.isOutlier) return null;

  const levelStyles = {
    minor: {
      bg: 'bg-amber-50 border-amber-200 text-amber-900',
      badgeBg: 'bg-amber-100 text-amber-800',
      badgeText: 'Minor Outlier',
      dot: 'bg-amber-500'
    },
    strong: {
      bg: 'bg-blue-50 border-blue-200 text-blue-900',
      badgeBg: 'bg-blue-100 text-blue-800',
      badgeText: 'Strong Outlier',
      dot: 'bg-blue-500'
    },
    extreme: {
      bg: 'bg-indigo-50/90 border-indigo-200 text-indigo-950',
      badgeBg: 'bg-indigo-600 text-white',
      badgeText: 'Extreme Outlier',
      dot: 'bg-indigo-500 animate-ping'
    }
  }[outlier.level || 'minor'];

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${levelStyles.bg}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${levelStyles.dot}`} />
        {levelStyles.badgeText} ({outlier.multiplier}×)
      </span>
    );
  }

  return (
    <div className={`rounded-3xl p-5 border ${levelStyles.bg} shadow-sm relative overflow-hidden`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <span className="flex h-2.5 w-2.5 relative">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${levelStyles.dot}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${levelStyles.dot}`} />
          </span>
          <span className="text-xs font-bold uppercase tracking-wider">Outlier Detected</span>
        </div>
        <span className={`text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full tracking-wide ${levelStyles.badgeBg}`}>
          {levelStyles.badgeText}
        </span>
      </div>

      <h4 className="text-base font-bold text-slate-900 mb-1">
        Growing {outlier.multiplier}× above 30-day baseline
      </h4>
      <p className="text-xs text-slate-600 mb-4 leading-relaxed">
        {outlier.summary || `Velocity increased ${outlier.growthVs30dAveragePct}% compared to historical rolling averages.`}
      </p>

      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200/60 text-xs">
        <div>
          <span className="text-slate-500 block text-[10px] uppercase font-medium">Normal baseline</span>
          <strong className="text-slate-800 font-semibold">{outlier.normalVelocity}</strong>
        </div>
        <div>
          <span className="text-slate-500 block text-[10px] uppercase font-medium">Current surge</span>
          <strong className="text-indigo-600 font-bold">{outlier.currentVelocity}</strong>
        </div>
      </div>
    </div>
  );
};
